import type { QuerySpec } from '@helios/powersync-client-core';

// ── Row types ──

export interface PatientDetailRow {
  id: string;
  patient_number: string | null;
  full_name: string | null;
  birth_date: string | null;
  sex: string | null;
  status: string | null;
  photo_url: string | null;
  last_visited_at: string | null;
  title: string | null;
  local_name: string | null;
  country_of_birth: string | null;
  marital_status: string | null;
  nationality: string | null;
  race: string | null;
  occupation: string | null;
  religion: string | null;
  assigned_doctor: string | null;
  blood_group_abo: string | null;
  blood_group_rh: string | null;
  drug_allergies_notes: string | null;
  food_allergies_notes: string | null;
  medical_notes: string | null;
  medical_alerts: string | null;
  medical_history: string | null;
  past_medication_notes: string | null;
  current_medication_notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PatientIdentifierRow {
  id: string;
  patient_id: string;
  id_type: string | null;
  id_value: string | null;
}

export interface PatientContactRow {
  id: string;
  patient_id: string;
  email: string | null;
  mobile_country_code: string | null;
  mobile_number: string | null;
  formal_telephone: string | null;
  formal_residential_address: string | null;
  formal_province_state: string | null;
  formal_district: string | null;
  formal_postal_code: string | null;
  office_telephone: string | null;
  office_address: string | null;
}

export interface PatientAllergyRow {
  id: string;
  patient_id: string;
  allergy_type: string | null;
  substance: string | null;
  reaction: string | null;
  severity: string | null;
  deleted_at: string | null;
}

export interface PatientEmergencyContactRow {
  id: string;
  patient_id: string;
  name: string | null;
  relationship: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
}

export interface PatientIntakeFormRow {
  id: string;
  patient_id: string;
  intake_form_definition_id: string;
  status: string | null;
  completed_at: string | null;
  updated_at: string | null;
}

export interface IntakeFormResponseRow {
  id: string;
  patient_intake_form_id: string;
  answers: string | null; // JSON text
}

// ── QuerySpecs ──

export function qPatientById(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patients', 'detail', patientId],
    sql: `SELECT * FROM patients WHERE id = ?`,
    params: [patientId],
    watch: ['patients'],
  };
}

export function qPatientIdentifiers(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_identifiers', 'by-patient', patientId],
    sql: `SELECT * FROM patient_identifiers WHERE patient_id = ? ORDER BY id`,
    params: [patientId],
    watch: ['patient_identifiers'],
  };
}

export function qPatientContact(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_contacts', 'by-patient', patientId],
    sql: `SELECT * FROM patient_contacts WHERE patient_id = ? ORDER BY id LIMIT 1`,
    params: [patientId],
    watch: ['patient_contacts'],
  };
}

export function qPatientAllergies(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_allergies', 'by-patient', patientId],
    sql: `SELECT * FROM patient_allergies WHERE patient_id = ? AND deleted_at IS NULL`,
    params: [patientId],
    watch: ['patient_allergies'],
  };
}

export function qPatientEmergencyContacts(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_emergency_contacts', 'by-patient', patientId],
    sql: `SELECT * FROM patient_emergency_contacts WHERE patient_id = ? ORDER BY id`,
    params: [patientId],
    watch: ['patient_emergency_contacts'],
  };
}

export function qLatestPatientIntakeForm(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_intake_forms', 'latest', patientId],
    sql: `SELECT * FROM patient_intake_forms WHERE patient_id = ? ORDER BY completed_at DESC LIMIT 1`,
    params: [patientId],
    watch: ['patient_intake_forms'],
  };
}

export function qIntakeFormResponse(patientIntakeFormId: string): QuerySpec<[string]> {
  return {
    key: ['intake_form_responses', 'by-form', patientIntakeFormId],
    sql: `SELECT * FROM intake_form_responses WHERE patient_intake_form_id = ? ORDER BY id LIMIT 1`,
    params: [patientIntakeFormId],
    watch: ['intake_form_responses'],
  };
}

/** No-op query that returns zero rows. Used when a conditional query has no valid ID yet. */
export function qNoOp(tag: string): QuerySpec<[]> {
  return {
    key: ['noop', tag],
    sql: `SELECT 1 WHERE 0`,
    params: [],
    watch: [],
  };
}
